import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import InputFloating from "@/components/atoms/InputFloating";
import PageWrapper from '@/components/atoms/PageWrapper';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from '@/components/ui/button.tsx';

import { Label } from '@/components/ui/label';
import { useSetupAccount } from '@/lib/useQuery/user/useAccountSetup';
// import { useUserData } from "@/lib/useQuery/user/useUserData";

export default function AccountSetupPage() {
  // const { data: userData, isLoading, isFetching } = useUserData();
  const navigate = useNavigate();

  const schema = z.object({
    first_name: z.string().min(1, { message: "First name is required" }),
    last_name: z.string().min(1, { message: "Last name is required" }),
    password: z.string().min(6, "Password Must be at least 6 characters long").max(30, "Password Must not be longer than 30 characters"),
    confirm: z.string().min(6, "Password Must be at least 6 characters long").max(30, "Password Must not be longer than 30 characters"),
  })
    .refine(data => data.password === data.confirm, {
      message: "Passwords do not match",
      path: ["confirm"],
    });
  type Inputs = z.infer<typeof schema>;

  const { isPending, mutateAsync: mutateSetupAccount } = useSetupAccount();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: zodResolver(schema),
  })
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      const result = await mutateSetupAccount(data);
      if (!result.error) {
        navigate('/welcome');
        SuccessPopup("Account setup successful");
      } else {
        const error = result.error.message || "An error occurred. Please try again later.";
        ErrorPopup(error);
      }

    } catch (err) {
      ErrorPopup("An error occurred. Please try again later.");
    }
  };

  // TODO: temporary solution! This shouldn't be here, it should be in a protected route. The navigate() above in onSubmit() function should be enough 
  // useEffect(() => {
  //   if(userData?.first_name) {
  //     navigate('/welcome');
  //   }
  // }, [userData, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  return (
    <>
      {isPending && (<LoaderWithBackdrop />)}

      <PageWrapper className={"max-w-screen-lg mt-[24px]"}>
        <div className="px-1 m-0 overflow-hidden d-flex flex-column flex-grow-1 flex-nowrap" style={{ minHeight: 'calc(100vh - 98px)' }}>
          <h1 className="mb-1 text-xl font-medium">Account Setup</h1>
          <p className="text-xs sm:text-sm">
            <i className="bi bi-info-circle"></i> You have been invited to participate in surveys. Your account has been created, but we need additional information for you to fully use the application. Please set your first name, last name, and password.
          </p>
          <hr className="my-3" />

          <form className="flex flex-wrap justify-center m-0 mb-3" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4 max-w-96 grow">
              <InputFloating
                showError
                type="text"
                label="First Name"
                register={register("first_name", { required: true })}
                errorMessage={errors.first_name?.message}
              />
              <InputFloating
                showError
                type="text"
                label="Last Name"
                register={register("last_name", { required: true })}
                errorMessage={errors.last_name?.message}
              />
              <div className="">
                <Label>Password</Label>
                <div className="relative">
                  <InputFloating
                    showError
                    type={showPassword ? "text" : "password"}
                    register={register("password", { required: true })}
                    errorMessage={errors.password?.message}
                  />
                  <button
                    type="button"
                    className="absolute justify-center transform translate-y-1/2 bottom-1/2 right-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`text-xl bi bi-eye${showPassword ? "-slash" : ""}`}></i>
                  </button>
                </div>
              </div>
              <InputFloating
                showError
                type={showPassword ? "text" : "password"}
                label="Confirm Password"
                register={register("confirm", { required: true })}
                errorMessage={errors.confirm?.message}
              />
              <div>
                <div className="w-full mx-auto sm:max-w-32">
                  <Button
                    type="submit"
                    className="w-100"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </PageWrapper>
    </>
  );
}
